/**
 * BookingReferencesCard — the right-rail card listing this booking's deep links
 * into the external systems it also lives in (BON-546), plus the gift cards that
 * paid toward it.
 *
 * Every external entry's label, reference text and URL come from the API, and
 * nothing here switches on `type`, so a new external system reaches this card
 * without a change to this file. Renders nothing when there are no references.
 */
import type { LucideIcon } from 'lucide-react';
import { ChevronRight, ExternalLink, Gift } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { BookingExternalLink, BookingGiftCardRedemption } from '../../api/bookingsV2';

import { cardCls, META } from './bookingCardShared';

// `dark:hover:` rather than a bare `dark:` for the resting border: Tailwind
// orders the `dark` variant after `hover`, so `dark:border-stone-700` would win
// over `hover:border-moss-400` and the hover would be dead in dark theme.
const rowCls =
  'flex items-center gap-3 px-4 py-3 rounded-lg border border-stone-200 dark:border-stone-700 transition no-underline hover:border-moss-400 hover:bg-moss-50/50 dark:hover:border-moss-400 dark:hover:bg-moss-900/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-moss-500';

function referenceRowContent(Icon: LucideIcon, label: string, reference: string | null) {
  return (
    <>
      <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-stone-100 dark:bg-stone-700/50 flex items-center justify-center text-stone-500 dark:text-stone-300">
        <Icon size={16} aria-hidden="true" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-bark-900 dark:text-stone-100 truncate">
          {label}
        </span>
        <span className="block text-xs text-stone-500 dark:text-stone-400 font-mono truncate">
          {reference ?? '—'}
        </span>
      </span>
    </>
  );
}

const chevron = (
  <ChevronRight size={16} className="flex-shrink-0 text-stone-400" aria-hidden="true" />
);

export function BookingReferencesCard({
  links,
  giftCards,
}: {
  links: BookingExternalLink[];
  giftCards: BookingGiftCardRedemption[];
}) {
  if (links.length === 0 && giftCards.length === 0) return null;
  return (
    <div className={cardCls}>
      <div className="px-5 py-4 border-b border-stone-200 dark:border-stone-700 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-bark-900 dark:text-stone-100">References</h2>
        {links.length > 0 && <span className={META}>External</span>}
      </div>
      <div className="p-4 space-y-2.5">
        {giftCards.map((giftCard) => (
          <Link
            key={giftCard.giftCardId}
            to={`/admin/gift-cards/${giftCard.giftCardId}`}
            className={rowCls}
          >
            {referenceRowContent(Gift, 'Gift card', giftCard.codeMasked)}
            {chevron}
          </Link>
        ))}
        {links.map((link) => (
          <a
            key={link.url}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={rowCls}
          >
            {referenceRowContent(ExternalLink, link.label, link.reference)}
            <span className="sr-only">(opens in a new tab)</span>
            {chevron}
          </a>
        ))}
      </div>
    </div>
  );
}
